import React, { useCallback, useRef, useState } from "react";
import {
  Box,
  HStack,
  Icon,
  IconButton,
  Image,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ImagePlus, Pencil, Plus, Video, X } from "lucide-react";
import { useLanguage } from "@/contexts/languageContext";
import { useApi } from "@/uses/useApi";
import objectUtil from "@/utils/objectUtil";

const ARTICLE_FILE_TYPES = [
  { value: "image", label: "Image", icon: ImagePlus },
  { value: "video", label: "Vidéo", icon: Video },
];

const VIDEO_PLATFORMS = [
  { name: "YouTube",      color: "#FF0000", pattern: /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]+)/, embed: (id) => `https://www.youtube.com/embed/${id}` },
  { name: "YouTube",      color: "#FF0000", pattern: /youtu\.be\/([a-zA-Z0-9_-]+)/, embed: (id) => `https://www.youtube.com/embed/${id}` },
  { name: "Dailymotion",  color: "#0066DC", pattern: /dailymotion\.com\/video\/([a-zA-Z0-9]+)/, embed: (id) => `https://www.dailymotion.com/embed/video/${id}` },
  { name: "Dailymotion",  color: "#0066DC", pattern: /dai\.ly\/([a-zA-Z0-9]+)/, embed: (id) => `https://www.dailymotion.com/embed/video/${id}` },
  { name: "Vimeo",        color: "#1AB7EA", pattern: /vimeo\.com\/(\d+)/, embed: (id) => `https://player.vimeo.com/video/${id}` },
];

const detectVideoPlatform = (url) => {
  if (!url) return null;
  for (const platform of VIDEO_PLATFORMS) {
    const match = url.match(platform.pattern);
    if (match) return { name: platform.name, color: platform.color, videoId: match[1], embedUrl: platform.embed(match[1]) };
  }
  return null;
};

const isVideoUrl = (url) => {
  if (!url) return false;
  return /^https?:\/\//i.test(url);
};

const ArticleFileReadOnly = ({ file, index, onEdit, onRemove }) => {
  const typeInfo = ARTICLE_FILE_TYPES.find((t) => t.value === file.type);

  const getFilename = (url) => {
    if (!url) return "";
    return url.split("/").pop();
  };

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      p={4}
      w="100%"
      opacity={0.9}
    >
      <HStack justify="space-between">
        <HStack gap={2} flex={1} overflow="hidden">
          {typeInfo && <Icon as={typeInfo.icon} boxSize={5} color="primary.500" />}
          <Text fontWeight="bold" fontSize="sm">
            {file.name || typeInfo?.label || "Fichier"}
          </Text>
          <Text fontSize="sm" isTruncated color="gray.500">
            {file.type === "video"
              ? file.content
              : getFilename(file.content)}
          </Text>
        </HStack>
        <HStack gap={1}>
          <IconButton size="sm" variant="ghost" onClick={() => onEdit(index)}>
            <Icon as={Pencil} boxSize={4} />
          </IconButton>
          <IconButton size="sm" variant="ghost" onClick={() => onRemove(index)}>
            <Icon as={X} boxSize={4} />
          </IconButton>
        </HStack>
      </HStack>
    </Box>
  );
};

const ArticleFileForm = ({ file, index, onChange, onRemove }) => {
  const api = useApi();
  const fileInputRef = useRef();

  const handleChange = (field, value) => {
    onChange(index, { ...file, [field]: value });
  };

  const handleFileUpload = useCallback((e) => {
    const files = e.target.files;
    if (!files || files.length !== 1) return;

    api.FileService.getPresignedURL(files).then((res) => {
      if (res?.data?.type === "success") {
        if (!objectUtil.isArray(res?.data?.value) || !res?.data?.value?.[0]?.presignedURL) {
          throw new Error("MISSING_PRESIGNED_URL");
        }
        if (!objectUtil.isArray(res?.data?.value) || !res?.data?.value?.[0]?.url) {
          throw new Error("MISSING_FILE_URL");
        }
        api.FileService.upload(res.data.value[0].presignedURL, files.item(0)).then(() => {
          onChange(index, {
            ...file,
            content: res.data.value[0].url,
          });
        }).catch(console.error);
      }
    }).catch(console.error);
  }, [file, index, onChange, api]);

  const getFilename = (url) => {
    if (!url) return "";
    return url.split("/").pop();
  };

  const platform = file.type === "video" ? detectVideoPlatform(file.content) : null;

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      p={4}
      w="100%"
    >
      <HStack justify="space-between" mb={3}>
        <Text fontWeight="bold">Fichier {index + 1}</Text>
        <IconButton
          size="sm"
          variant="ghost"
          onClick={() => onRemove(index)}
        >
          <Icon as={X} boxSize={4} />
        </IconButton>
      </HStack>

      <VStack gap={3} align="stretch">
        <Box>
          <Text mb={1} fontSize="sm">Type</Text>
          <HStack gap={2}>
            {ARTICLE_FILE_TYPES.map((type) => (
              <Box
                key={type.value}
                as="button"
                type="button"
                px={4}
                py={2}
                borderRadius="md"
                border="1px solid"
                borderColor={file.type === type.value ? "primary.500" : "gray.200"}
                bg={file.type === type.value ? "primary.500" : "transparent"}
                color={file.type === type.value ? "white" : "inherit"}
                cursor="pointer"
                display="flex"
                alignItems="center"
                gap={2}
                onClick={() => handleChange("type", type.value)}
              >
                <Icon as={type.icon} boxSize={4} />
                {type.label}
              </Box>
            ))}
          </HStack>
        </Box>

        <Box>
          <Text mb={1} fontSize="sm">Nom</Text>
          <Input
            placeholder="Nom du fichier"
            value={file.name || ""}
            onChange={(e) => handleChange("name", e.target.value)}
          />
        </Box>

        {file.type === "video" && (
          <Box>
            <Text mb={1} fontSize="sm">URL de la vidéo (YouTube, Dailymotion, Vimeo)</Text>
            <Input
              placeholder="https://www.youtube.com/watch?v=..."
              value={file.content || ""}
              onChange={(e) => handleChange("content", e.target.value)}
            />
            {platform && (
              <HStack mt={2} gap={2} alignItems="center">
                <Box
                  px={2} py={0.5}
                  borderRadius="md"
                  bg={platform.color}
                  color="white"
                  fontSize="xs"
                  fontWeight="bold"
                >
                  {platform.name}
                </Box>
                <Text fontSize="xs" color="green.500">✓ Lien reconnu</Text>
              </HStack>
            )}
            {file.content && isVideoUrl(file.content) && !platform && (
              <Text fontSize="xs" color="red.500" mt={1}>
                Lien non supporté. Seuls YouTube, Dailymotion et Vimeo sont acceptés.
              </Text>
            )}
            {file.content && !isVideoUrl(file.content) && file.content.length > 3 && (
              <Text fontSize="xs" color="orange.500" mt={1}>
                Veuillez entrer une URL valide (commençant par https://)
              </Text>
            )}
            {platform?.embedUrl && (
              <Box mt={2} borderRadius="md" overflow="hidden">
                <iframe
                  src={platform.embedUrl}
                  width="100%"
                  height="250"
                  style={{ border: "none", borderRadius: "8px" }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={`Vidéo ${index + 1}`}
                />
              </Box>
            )}
          </Box>
        )}

        {file.type === "image" && (
          <Box>
            <Input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              display="none"
            />
            {file.content ? (
              <VStack align="stretch" gap={2}>
                <Image
                  src={file.content}
                  alt={`Fichier ${index + 1}`}
                  maxH="150px"
                  objectFit="contain"
                  borderRadius="md"
                />
                <HStack>
                  <Text fontSize="sm" isTruncated flex={1}>
                    {getFilename(file.content)}
                  </Text>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      handleChange("content", null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                  >
                    <Icon as={X} boxSize={4} />
                  </IconButton>
                </HStack>
              </VStack>
            ) : (
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                backgroundColor="secondary.500"
                width="100%"
              >
                <Icon as={ImagePlus} boxSize={5} />
                <Text ml={2}>Choisir une image</Text>
              </IconButton>
            )}
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export const ArticleFilesInput = ({ files = [], onChange, error }) => {
  const { t } = useLanguage();
  const [editingIndices, setEditingIndices] = useState(new Set());

  const isExisting = (file) => {
    const hasName = typeof file.name === 'string' && file.name.length > 0;
    if (file.type === "video") {
      return hasName && file.content;
    }
    return hasName && file.type && file.content;
  };

  const addFile = () => {
    const newIndex = files.length;
    setEditingIndices((prev) => new Set(prev).add(newIndex));
    onChange([...files, { type: "", content: null, name: "" }]);
  };

  const updateFile = (index, updated) => {
    const next = [...files];
    next[index] = updated;
    onChange(next);
  };

  const removeFile = (index) => {
    setEditingIndices((prev) => {
      const next = new Set();
      prev.forEach((i) => {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      });
      return next;
    });
    onChange(files.filter((_, i) => i !== index));
  };

  const enableEdit = (index) => {
    setEditingIndices((prev) => new Set(prev).add(index));
  };

  return (
    <VStack gap={4} align="stretch" w="100%">
      {files.map((file, index) => {
        const editing = editingIndices.has(index) || !isExisting(file);
        return editing ? (
          <ArticleFileForm
            key={index}
            index={index}
            file={file}
            onChange={updateFile}
            onRemove={removeFile}
          />
        ) : (
          <ArticleFileReadOnly
            key={index}
            index={index}
            file={file}
            onEdit={enableEdit}
            onRemove={removeFile}
          />
        );
      })}
      <IconButton
        onClick={addFile}
        backgroundColor="secondary.500"
        width={256}
      >
        <Text>{t("sentences.selectFiles")}</Text>
        <Icon as={Plus} color="gray.550" boxSize={5} />
      </IconButton>
      {error && (
        <Text color="red.500" fontSize="sm">{error}</Text>
      )}
    </VStack>
  );
};

export default ArticleFilesInput;
